import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Save, Loader2, Youtube, Star, Play, Video, Eye,
} from "lucide-react";
import api from "../../lib/api";

const STATUS = {
  draft: { label: "Brouillon", cls: "bg-foreground/10 text-foreground/70" },
  published: { label: "Publiée", cls: "bg-emerald-100 text-emerald-700" },
  archived: { label: "Archivée", cls: "bg-orange-100 text-orange-700" },
};

const V_DEFAULTS = {
  title: "", description: "", youtube_url: "", category: "Conférences",
  thumbnail: "", duration: "", speakers: [], tags: [], status: "published",
  published_at: "", is_featured: false,
};

export default function WebTVAdmin() {
  const [videos, setVideos] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/admin/webtv/videos").then((r) => setVideos(r.data));
    api.get("/webtv/categories").then((r) => setCats(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    try {
      const payload = {
        ...f,
        speakers: (f.speakers || []).filter(Boolean),
        tags: (f.tags || []).filter(Boolean),
      };
      if (editing?.id) await api.put(`/admin/webtv/videos/${editing.id}`, payload);
      else await api.post("/admin/webtv/videos", payload);
      toast.success("Vidéo enregistrée");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!confirm("Supprimer cette vidéo ?")) return;
    await api.delete(`/admin/webtv/videos/${id}`);
    load();
  };
  const toggleFeatured = async (id) => { await api.post(`/admin/webtv/videos/${id}/toggle-featured`); load(); };

  const filtered = videos.filter((v) => {
    if (filter !== "all" && v.status !== filter) return false;
    if (q) {
      const s = q.toLowerCase();
      return (v.title || "").toLowerCase().includes(s) || (v.category || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="webtv-admin">
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Rechercher</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titre ou catégorie…" className="inp" data-testid="webtv-search" />
        </div>
        <div>
          <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Statut</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="inp bg-white" data-testid="webtv-status-filter">
            <option value="all">Tous</option>
            <option value="published">Publiée</option>
            <option value="draft">Brouillon</option>
            <option value="archived">Archivée</option>
          </select>
        </div>
        <button onClick={() => setEditing({ ...V_DEFAULTS, category: cats[0]?.name || "Conférences" })}
                className="btn-primary" data-testid="webtv-create">
          <Plus className="w-4 h-4" /> Nouvelle vidéo
        </button>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bone text-foreground/70 text-[0.65rem] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Vignette</th>
              <th className="text-left px-4 py-2.5 font-semibold">Titre</th>
              <th className="text-left px-4 py-2.5 font-semibold">Catégorie</th>
              <th className="text-left px-4 py-2.5 font-semibold">Statut</th>
              <th className="text-left px-4 py-2.5 font-semibold">Vues</th>
              <th className="text-left px-4 py-2.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => {
              const st = STATUS[v.status] || STATUS.draft;
              return (
                <tr key={v.id} className="border-t border-border" data-testid={`webtv-row-${v.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="w-20 aspect-video rounded-sm overflow-hidden bg-ink/5">
                      {v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 max-w-sm">
                    <div className="font-semibold text-ink truncate">{v.title}</div>
                    <div className="text-[0.7rem] text-foreground/50 mt-0.5 truncate">{v.duration}</div>
                  </td>
                  <td className="px-4 py-2.5">{v.category}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs flex items-center gap-1.5"><Eye className="w-3 h-3 text-foreground/40" /> {v.view_count || 0}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleFeatured(v.id)}
                              title={v.is_featured ? "Retirer À la une" : "Mettre à la une"}
                              className={`p-1.5 rounded-sm ${v.is_featured ? "bg-gold text-ink" : "text-foreground/50 hover:text-ink"}`}
                              data-testid={`webtv-featured-${v.id}`}>
                        <Star className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditing(v)} className="p-1.5 text-foreground/50 hover:text-ink" data-testid={`webtv-edit-${v.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(v.id)} className="p-1.5 text-foreground/50 hover:text-crimson" data-testid={`webtv-del-${v.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground/55">Aucune vidéo.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <VideoFormModal
          key={editing.id || "new"}
          value={editing}
          categories={cats}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function VideoFormModal({ value, categories, onClose, onSave }) {
  const [f, setF] = useState(value);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.title?.trim()) return toast.error("Titre obligatoire");
    if (!f.youtube_url?.trim()) return toast.error("URL YouTube obligatoire");
    setSaving(true);
    try { await onSave(f); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" />
      <div className="relative bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white z-10 border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <div className="eyebrow text-gold-dark">{f.id ? "Modification" : "Nouvelle vidéo"}</div>
            <h3 className="font-serif text-xl text-ink mt-0.5">{f.title || "Sans titre"}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-foreground/60" /></button>
        </div>

        <div className="p-6 space-y-5">
          <FieldLabel label="URL YouTube *">
            <div className="relative">
              <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-crimson" />
              <input value={f.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)}
                     className="inp pl-9" placeholder="https://www.youtube.com/watch?v=..." data-testid="video-yt" />
            </div>
            <div className="text-[0.7rem] text-foreground/55 mt-1">Formats acceptés : watch?v=, youtu.be/, embed/, shorts/</div>
          </FieldLabel>

          <FieldLabel label="Titre *">
            <input value={f.title || ""} onChange={(e) => set("title", e.target.value)} className="inp" data-testid="video-title" />
          </FieldLabel>

          <div className="grid md:grid-cols-2 gap-4">
            <FieldLabel label="Catégorie">
              <select value={f.category || ""} onChange={(e) => set("category", e.target.value)} className="inp bg-white" data-testid="video-category">
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="Durée (optionnel)">
              <input value={f.duration || ""} onChange={(e) => set("duration", e.target.value)} className="inp" placeholder="12:34" data-testid="video-duration" />
            </FieldLabel>
          </div>

          <FieldLabel label="Description">
            <textarea rows={3} value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="inp" data-testid="video-desc" />
          </FieldLabel>

          <FieldLabel label="Intervenants (séparés par virgule)">
            <input value={(f.speakers || []).join(", ")} onChange={(e) => set("speakers", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                   className="inp" data-testid="video-speakers" placeholder="Nom Prénom, Nom Prénom…" />
          </FieldLabel>

          <FieldLabel label="Tags (séparés par virgule)">
            <input value={(f.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                   className="inp" data-testid="video-tags" />
          </FieldLabel>

          <FieldLabel label="Miniature personnalisée (URL, optionnelle)">
            <input value={f.thumbnail || ""} onChange={(e) => set("thumbnail", e.target.value)}
                   className="inp" placeholder="Auto-détectée depuis YouTube si vide" data-testid="video-thumbnail" />
          </FieldLabel>

          <div className="grid md:grid-cols-2 gap-4">
            <FieldLabel label="Statut">
              <select value={f.status || "published"} onChange={(e) => set("status", e.target.value)} className="inp bg-white" data-testid="video-status">
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
                <option value="archived">Archivé</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Programmer la publication">
              <input type="datetime-local" value={toLocalDT(f.published_at)}
                     onChange={(e) => set("published_at", fromLocalDT(e.target.value))} className="inp" data-testid="video-scheduled" />
            </FieldLabel>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!f.is_featured} onChange={(e) => set("is_featured", e.target.checked)} data-testid="video-featured-cb" />
            <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-gold-dark" /> À la une (une seule vidéo à la fois)</span>
          </label>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-border px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm">Annuler</button>
          <button onClick={submit} disabled={saving} className="btn-primary text-sm" data-testid="video-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
function toLocalDT(iso) {
  if (!iso) return "";
  try { const d = new Date(iso); const p = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch { return ""; }
}
function fromLocalDT(local) { try { return local ? new Date(local).toISOString() : ""; } catch { return ""; } }
